import Box from '@mui/joy/Box'
import Button from '@mui/joy/Button'
import Typography from '@mui/joy/Typography'
import PersonIcon from '@mui/icons-material/Person'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import type { MutableRefObject } from 'react'
import { useTranslation } from 'react-i18next'

import type { CheckoutFormValues } from '@/components/checkout/page/controller'
import type { CheckoutMode } from '@/components/checkout/page/types'
import type { CheckoutContactInfoValues } from '@/types/reservation-checkout'
import { getInvoiceCountryName } from '@/components/checkout/country-options'
import type { EmailDomainSuggestion } from '@/components/checkout/contact-info/email-domain-suggestion'
import CheckoutContactInfoForm from './form'
import { type Control, type UseFormSetValue, useWatch } from 'react-hook-form'
import type { CountryCode } from 'libphonenumber-js'

type CheckoutContactInfoProps = {
    desktop?: boolean
    mode?: CheckoutMode
    control: Control<CheckoutFormValues>
    setValue: UseFormSetValue<CheckoutFormValues>
    summaryValues: CheckoutContactInfoValues
    invoiceRequired: boolean
    shouldEdit: boolean
    defaultPhoneCountry: CountryCode
    hideActions?: boolean
    requestInProgress?: boolean
    errorMessage?: string | null
    emailDomainSuggestion: EmailDomainSuggestion | null
    sectionRef?: MutableRefObject<HTMLDivElement | null>
    onEdit: () => void
    onCancel: () => void
    onClear: () => void
    onPhoneCountryChange: (country: CountryCode) => void
    onCheckEmailDomainSuggestion: (email: string) => void
    onClearEmailDomainSuggestion: () => void
    onSave: () => Promise<void>
}

export default function CheckoutContactInfo({
    desktop,
    mode = 'sectioned',
    control,
    setValue,
    summaryValues,
    invoiceRequired,
    shouldEdit,
    defaultPhoneCountry,
    hideActions = false,
    requestInProgress = false,
    errorMessage,
    emailDomainSuggestion,
    sectionRef,
    onEdit,
    onCancel,
    onClear,
    onPhoneCountryChange,
    onCheckEmailDomainSuggestion,
    onClearEmailDomainSuggestion,
    onSave,
}: CheckoutContactInfoProps) {
    const { t, i18n } = useTranslation()
    const isSingleSubmit = mode === 'single_submit'
    const shouldShowInvoiceDetails = summaryValues.requested_invoice
    const invoiceCountryName = getInvoiceCountryName(
        summaryValues.invoice_country,
        i18n.language,
        summaryValues.invoice_country,
    )
    const showPhoneNumber2 = useWatch({
        control,
        name: 'show_phone_number_2',
    })

    return (
        <Box
            ref={sectionRef}
            sx={{
                scrollMarginBlock: 'calc(70px + 1rem)',
                display: 'flex',
                flexDirection: 'column',
                gap: {
                    xs: 0.5,
                    sm: 1,
                },
                padding: '0.5rem 1rem 1rem',
                borderRadius: 10,
                border: '1px solid var(--joy-palette-neutral-100)',
            }}
        >
            <Box sx={{
                display: 'flex',
                gap: 2,
            }}>
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    flexGrow: 1,
                    alignItems: 'center',
                }}>
                    <PersonIcon sx={{ color: '#484848', fontSize: desktop ? '24px' : '20px' }} />
                    <Typography
                        level={desktop ? 'body-lg' : 'body-md'}
                        sx={{
                            fontWeight: 600,
                        }}
                    >
                        {t('checkout.contactInfo.heading')}
                    </Typography>
                </Box>
                {!shouldEdit ? (
                    <Button
                        variant="soft"
                        onClick={onEdit}
                        sx={{
                            color: 'black',
                            backgroundColor: 'var(--joy-palette-neutral-50)',
                            '&:hover': {
                                color: 'black',
                                backgroundColor: 'var(--joy-palette-neutral-100)',
                            },
                            borderRadius: 7,
                            border: '1px solid var(--joy-palette-neutral-200)',
                            mr: -1,
                        }}
                    >
                        {t('checkout.contactInfo.edit')}
                    </Button>
                ) : !isSingleSubmit ? <Box height={40} /> : null}
            </Box>
            {shouldEdit ? (
                <CheckoutContactInfoForm
                    control={control}
                    invoiceRequired={invoiceRequired}
                    showPhoneNumber2={showPhoneNumber2}
                    defaultPhoneCountry={defaultPhoneCountry}
                    emailDomainSuggestion={emailDomainSuggestion}
                    onCheckEmailDomainSuggestion={onCheckEmailDomainSuggestion}
                    onClearEmailDomainSuggestion={onClearEmailDomainSuggestion}
                    onShowPhoneNumber2={() => {
                        setValue('show_phone_number_2', true)
                    }}
                    onCancel={onCancel}
                    onClear={onClear}
                    onPhoneCountryChange={onPhoneCountryChange}
                    onSubmit={event => {
                        event.preventDefault()
                        void onSave()
                    }}
                    hideActions={hideActions}
                    requestInProgress={requestInProgress}
                    errorMessage={errorMessage}
                />
            ) : (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                }}>
                    {summaryValues.name ? (
                        <Typography
                            level={desktop ? 'body-md' : 'body-sm'}
                            sx={{ fontWeight: 600 }}
                        >
                            {summaryValues.name}
                        </Typography>
                    ) : null}
                    {summaryValues.email ? (
                        <Typography level={desktop ? 'body-md' : 'body-sm'}>
                            {summaryValues.email}
                        </Typography>
                    ) : null}
                    {summaryValues.phone_number ? (
                        <Typography level={desktop ? 'body-md' : 'body-sm'}>
                            {summaryValues.phone_number}
                        </Typography>
                    ) : null}
                    {summaryValues.phone_number_2 ? (
                        <Typography level={desktop ? 'body-md' : 'body-sm'}>
                            {summaryValues.phone_number_2}
                        </Typography>
                    ) : null}
                    {shouldShowInvoiceDetails ? (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                            pt: desktop ? 2.5 : 1.5,
                        }}>
                            <Box sx={{
                                display: 'flex',
                                gap: 1,
                                alignItems: 'center',
                                pb: 1,
                            }}>
                                <ReceiptLongIcon
                                    sx={{
                                        color: '#484848',
                                        fontSize: desktop ? '20px' : '18px',
                                    }}
                                />
                                <Typography
                                    level={desktop ? 'body-lg' : 'body-md'}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {t('checkout.contactInfo.invoiceHeading')}
                                </Typography>
                            </Box>
                            {summaryValues.invoice_title ? (
                                <Typography
                                    level={desktop ? 'body-md' : 'body-sm'}
                                    sx={{ fontWeight: 600 }}
                                >
                                    {summaryValues.invoice_title}
                                </Typography>
                            ) : null}
                            {summaryValues.invoice_street ? (
                                <Typography level={desktop ? 'body-md' : 'body-sm'}>
                                    {summaryValues.invoice_street}
                                </Typography>
                            ) : null}
                            {summaryValues.invoice_city || summaryValues.invoice_zip_code || invoiceCountryName ? (
                                <Typography level={desktop ? 'body-md' : 'body-sm'}>
                                    {[
                                        [summaryValues.invoice_zip_code, summaryValues.invoice_city].filter(Boolean).join(' '),
                                        invoiceCountryName,
                                    ].filter(Boolean).join(', ')}
                                </Typography>
                            ) : null}
                            {summaryValues.invoice_ic ? (
                                <Typography level={desktop ? 'body-md' : 'body-sm'} mt={0.5}>
                                    {t('checkout.contactInfo.invoiceIc')}: {summaryValues.invoice_ic}
                                </Typography>
                            ) : null}
                            {summaryValues.invoice_dic ? (
                                <Typography level={desktop ? 'body-md' : 'body-sm'}>
                                    {t('checkout.contactInfo.invoiceDic')}: {summaryValues.invoice_dic}
                                </Typography>
                            ) : null}
                        </Box>
                    ) : null}
                </Box>
            )}
        </Box>
    )
}
